import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Field } from 'react-final-form';

import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { IKnmSubject248ItAccreditationResponse } from 'APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { KnmSonkoField } from 'KnmPackage/MainModule/components/KnmSonkoField';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { ErknmFieldMark } from 'KnmErknmPackage/MainModule/components/ErknmFieldMark';

import {
  COUNTRY_BLOCK_ID,
  DOCUMENT_BLOCK_ID,
  INN_LABEL_BLOCK_ID,
  KPP_BLOCK_ID,
  OGRN_LABEL_BLOCK_ID,
  RAFP_NZA_BLOCK_ID,
  SNILS_BLOCK_ID,
} from '../constants/knmSubjectFieldsElementsIds';
import styles from './KnmSubject.module.scss';
import { KnmSubjectItAccreditation } from './KnmSubjectItAccreditation';
import { KnmSubjectIsNko } from './KnmSubjectNko';
import { KnmMspField } from '../../MainModule/components/KnmMspField';
import {
  isFnsAddressFnsError,
  isOrganizationNameFnsError,
  isWorkAddressFnsError,
  renderFnsCheckMessage,
  renderFnsErvkEqualsError,
} from '../../../CorePackage/MainModule/common/renderFnsMessage';

export interface IKnmSubjectProps {
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  inn: string | undefined;
  ogrn: string | undefined;
  snils: string | undefined;
  isFiz: boolean | undefined;
  rafpNza: string | undefined;
  kpp: string | undefined;
  organizationAutocomplete?: boolean;
  watchOnly: boolean;
  checkSubjectResult?: IKnmCheckSubjectResult | null;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  name?: string;
  subjectTypeLabel?: string;
  getBlockId: (id: string) => string;
  mspCode?: string;
  mspRegistryDate?: string;
  isSonko?: boolean;
  formRef: IKnmFormRef | undefined;
  subjectTypeOptions?: IDictionarySelectOption<ITargetEntity>[];
  isIt?: boolean;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  ervkId: string | undefined;
}

export interface IKnmSubjectRef {
  onClear: () => void;
}

export const KnmFounder = forwardRef<IKnmSubjectRef, IKnmSubjectProps>(
  (
    {
      subjectType,
      subjectTypeOptions,
      formRef,
      inn,
      ogrn,
      snils,
      rafpNza,
      kpp,
      organizationAutocomplete,
      watchOnly,
      checkSubjectResult,
      isFiz,
      smevRequestStatus,
      mspCode,
      mspRegistryDate,
      isSonko,
      name: parentName,
      subjectTypeLabel = 'Тип контролируемого лица',
      getBlockId,
      isIt,
      itAccreditationResponse,
      ervkId,
    },
    ref,
  ) => {
    const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

    const name = parentName ? `${parentName}.` : '';

    const subjectTypeFieldName = `${name}subjectTypeKnm` as keyof IKnmForm;
    const innFieldName = `${name}inn` as keyof IKnmForm;
    const snilsFieldName = `${name}snils` as keyof IKnmForm;
    const organizationNameFieldName = `${name}organizationName` as keyof IKnmForm;
    const ogrnFieldName = `${name}ogrn` as keyof IKnmForm;
    const locationAddressFieldName = `${name}locationAddress` as keyof IKnmForm;
    const factualAddressFieldName = `${name}factualAddress` as keyof IKnmForm;
    const documentFieldName = `${name}document` as keyof IKnmForm;
    const countryFieldName = `${name}country` as keyof IKnmForm;
    const rafpNzaFieldName = `${name}rafpNza` as keyof IKnmForm;
    const kppFieldName = `${name}kpp` as keyof IKnmForm;
    const isFizFieldName = `${name}isFiz` as keyof IKnmForm;
    const nkoForeignAgentsRegistryNumberFieldName = `${name}nkoForeignAgentsRegistryNumber` as keyof IKnmForm;
    const nkoForeignAgentsRegistryInclusionDateFieldName = `${name}nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm;
    const nkoForeignAgentsRegistryExclusionDateFieldName = `${name}nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm;
    const isNkoFieldName = `${name}isNko` as keyof IKnmForm;
    const mspCodeFieldName = `${name}mspCode` as keyof IKnmForm;
    const mspRegistryDateFieldName = `${name}mspRegistryDate` as keyof IKnmForm;
    const isSonkoName = `${name}isSonko` as keyof IKnmForm;
    const organizationAutocompleteName = `${name}organizationAutocomplete` as keyof IKnmForm;

    const checkSubjectResultName = `${name}checkSubjectResult` as keyof IKnmForm;
    const dateCreateName = `${name}dateCreate` as keyof IKnmForm;
    const firstOrganizationNameName = `${name}firstOrganizationName` as keyof IKnmForm;
    const organizationGuidName = `${name}organizationGuid` as keyof IKnmForm;

    const smevItAccreditationRequestStatusName = `${name}smevItAccreditationRequestStatus` as keyof IKnmForm;
    const isPresentInItRegistryName = `${name}isPresentInItRegistry` as keyof IKnmForm;
    const itAccreditationResponseName = `${name}itAccreditationResponse` as keyof IKnmForm;

    const subjectTypeBlockId = getBlockId(subjectTypeFieldName);
    const countryBlockId = getBlockId(COUNTRY_BLOCK_ID);
    const documentBlockId = getBlockId(DOCUMENT_BLOCK_ID);
    const innLabelBlockId = getBlockId(INN_LABEL_BLOCK_ID);
    const kppBlockId = getBlockId(KPP_BLOCK_ID);
    const ogrnLabelBlockId = getBlockId(OGRN_LABEL_BLOCK_ID);
    const rafpNzaBlockId = getBlockId(RAFP_NZA_BLOCK_ID);
    const snilsBlockId = getBlockId(SNILS_BLOCK_ID);

    const isRafpNzaSent = smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
    const isRafpNzaNotFound = smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
    const isRafpNzaError = smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
    const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
    const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;
    const isIP = subjectType?.label === KnmSubjectTypesEnum.IP;
    const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
    const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;
    const isIUR = subjectType?.label === KnmSubjectTypesEnum.IUR;

    const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
    const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);

    const isOrganizationNameFieldEnabled = !watchOnly && isFiz;
    const isShowAutoCompleteField = isUR || isIP;

    const resetFields = useCallback(() => {
      formRef?.current.change(innFieldName);
      formRef?.current.change(snilsFieldName);
      formRef?.current.change(organizationNameFieldName);
      formRef?.current.change(isFizFieldName);
      formRef?.current.change(ogrnFieldName);
      formRef?.current.change(locationAddressFieldName);
      formRef?.current.change(countryFieldName);
      formRef?.current.change(kppFieldName);
      formRef?.current.change(rafpNzaFieldName);
      formRef?.current.change(documentFieldName);
      formRef?.current.change(nkoForeignAgentsRegistryNumberFieldName);
      formRef?.current.change(nkoForeignAgentsRegistryInclusionDateFieldName);
      formRef?.current.change(nkoForeignAgentsRegistryExclusionDateFieldName);
      formRef?.current.change(isNkoFieldName);
      formRef?.current.change(mspCodeFieldName);
      formRef?.current.change(mspRegistryDateFieldName);
      formRef?.current.change(isSonkoName);
      formRef?.current.change(organizationGuidName);
      formRef?.current.change(firstOrganizationNameName);
      formRef?.current.change(dateCreateName);
      formRef?.current.change(checkSubjectResultName);
      formRef?.current.change(smevItAccreditationRequestStatusName);
      formRef?.current.change(isPresentInItRegistryName);
      formRef?.current.change(itAccreditationResponseName);
    }, [formRef]);

    useEffect(() => {
      if (subjectTypeOptions && subjectType && !subjectTypeOptions.find(item => item.id === subjectType.label)) {
        formRef?.current.change(subjectTypeFieldName);
      }
    }, [subjectTypeOptions, subjectType, formRef]);

    const handleLegalEntityAutoCompleteSelect = useCallback(
      (legalEntityLookup: ILegalEntityLookup) => {
        const {
          ogrn: legalEntityOgrn,
          inn: legalEntityInn,
          orgName: legalEntityOrgName,
          leAddress: legalEntityLeAddress,
          mspCategory: legalMspCategory,
          mspRegistryDate: legalMspRegistryDate,
        } = legalEntityLookup;

        formRef?.current.change(organizationAutocompleteName, true);
        if (legalEntityOgrn) {
          formRef?.current.change(ogrnFieldName, legalEntityOgrn);
        }
        if (legalEntityInn) {
          formRef?.current.change(innFieldName, legalEntityInn);
        }
        if (legalEntityOrgName) {
          formRef?.current.change(organizationNameFieldName, legalEntityOrgName);
        }
        if (legalEntityLeAddress && isUR) {
          formRef?.current.change(locationAddressFieldName, legalEntityLeAddress);
        }
        if (legalEntityLeAddress && isIP) {
          formRef?.current.change(factualAddressFieldName, legalEntityLeAddress);
        }
        if (legalMspCategory) {
          formRef?.current.change(
            mspCodeFieldName,
            mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum],
          );
        }
        if (legalMspRegistryDate) {
          formRef?.current.change(mspRegistryDateFieldName, legalMspRegistryDate);
        }
      },
      [formRef, isUR, isIP],
    );

    const handleAutoCompleteClear = useCallback(() => {
      formRef?.current.change(ogrnFieldName);
      formRef?.current.change(innFieldName);
      formRef?.current.change(organizationNameFieldName);
      formRef?.current.change(locationAddressFieldName);
      formRef?.current.change(factualAddressFieldName);
      dispatchKnmSetLegalEntityData(undefined);
      formRef?.current.change(organizationAutocompleteName, false);
    }, [dispatchKnmSetLegalEntityData, formRef]);

    const onClear = useCallback(() => {
      formRef?.current.change(subjectTypeFieldName);
      resetFields();
      handleAutoCompleteClear();
    }, []);

    useImperativeHandle(
      ref,
      () => {
        return {
          onClear,
        };
      },
      [],
    );

    const subjectTypeChange = useCallback(
      (value: IDictionarySelectOption<ITargetEntity> | undefined) => {
        resetFields();
        handleAutoCompleteClear();
        formRef?.current.change(isFizFieldName, value?.label === KnmSubjectTypesEnum.FIZ);
      },
      [resetFields, formRef],
    );

    const normalizeFio = (value: string | undefined) => {
      if (!value) return value;

      if (isFIZ) {
        return value.replace(/[^а-яё\d- ]/gi, '');
      }
      if (isIFIZ) {
        return value.replace(/[^а-яёa-z-`' ]/gi, '');
      }

      return value;
    };

    const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);
    const waitFromRafpWarning = isRafpFilled ? 'Сохраните ПМ и ожидайте загрузки информации из РАФП' : undefined;
    const innOrSnilsWarning = isFiz && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
    const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
    const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

    const isItText = () => {
      if (isIt === undefined) {
        return '';
      }

      return isIt ? 'Да' : 'Нет';
    };

    return (
      <>
        <Field name={checkSubjectResultName}>{() => null}</Field>
        <Field name={dateCreateName}>{() => null}</Field>
        <Field name={firstOrganizationNameName}>{() => null}</Field>
        <Field name={organizationGuidName}>{() => null}</Field>

        <Row>
          <FieldBlock label={subjectTypeLabel} size="half" color="black">
            <SelectInputField
              id={subjectTypeBlockId}
              name={subjectTypeFieldName}
              options={subjectTypeOptions}
              size="large"
              onChange={subjectTypeChange}
              disabled={watchOnly}
            />
          </FieldBlock>
        </Row>
        {subjectType && (
          <>
            {!isIFIZ && !isIUR && (
              <Row>
                {(isUR || isIP) && (
                  <FieldBlock
                    label={`Основной государственный регистрационный номер ${isIP ? '(ОГРНИП)' : '(ОГРН)'}`}
                    size="half"
                    color="black"
                    id={ogrnLabelBlockId}
                  >
                    <KnmOgrnField
                      name={ogrnFieldName}
                      clearable={organizationAutocomplete}
                      disabled={watchOnly || organizationAutocomplete}
                      ogrn={ogrn}
                      onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                      onClear={handleAutoCompleteClear}
                      onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                    />
                  </FieldBlock>
                )}
                <FieldBlock
                  label={
                    <>
                      Идентификационный номер налогоплательщика (ИНН)
                      <ErknmFieldMark fieldName={innFieldName} />
                    </>
                  }
                  size="half"
                  color="black"
                  id={innLabelBlockId}
                >
                  <KnmInnField
                    name={innFieldName}
                    clearable={organizationAutocomplete}
                    disabled={watchOnly || organizationAutocomplete || (isFIUR && !!rafpNza)}
                    inn={inn}
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    setLookupData={setLookupData}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    isFiz={isFIZ}
                    warning={
                      innOrSnilsWarning ||
                      waitFromRafpWarning ||
                      (!isFiz &&
                        !smevRequestStatus &&
                        renderFnsCheckMessage(checkSubjectResult, lookupData, organizationAutocomplete))
                    }
                    isShowAutoCompleteField={isShowAutoCompleteField}
                  />
                </FieldBlock>
                {isFIZ && (
                  <FieldBlock
                    label="Страховой номер индивидуального лицевого счета (СНИЛС)"
                    size="half"
                    color="black"
                    id={snilsBlockId}
                  >
                    <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={watchOnly} />
                  </FieldBlock>
                )}
                {isFIUR && (
                  <FieldBlock label="Номер записи об аккредитации в РАФП" size="half" color="black" id={rafpNzaBlockId}>
                    <TextInputField
                      name={rafpNzaFieldName}
                      disabled={watchOnly || !!inn || !!kpp}
                      warning={waitFromRafpWarning}
                    />
                    {isRafpNzaSent && <Hint text="Ожидайте загрузки информации из РАФП" />}
                    {isRafpNzaNotFound && (
                      <div className={styles.RafpNotFound}>
                        Запрашиваемые сведения отсутствуют в РАФП, проверьте введенные значения. Если у данного
                        иностранного юридического лица нет филиалов и представительств, зарегистрированных на территории
                        РФ, то нажмите галочку под типом контролируемого лица
                      </div>
                    )}
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </FieldBlock>
                )}
              </Row>
            )}
            {isFIUR && (
              <Row>
                <FieldBlock label="Код причины постановки на учет (КПП)" size="half" color="black" id={kppBlockId}>
                  <TextInputField name={kppFieldName} disabled={watchOnly || !!rafpNza} warning={waitFromRafpWarning} />
                </FieldBlock>
              </Row>
            )}
            {(isFIZ || isIFIZ) && (
              <Row>
                <FieldBlock label="Фамилия, имя, отчество (при наличии)" color="black">
                  <TextareaField
                    disabled={watchOnly}
                    name={organizationNameFieldName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    parse={normalizeFio}
                  />
                </FieldBlock>
              </Row>
            )}
            {(isUR || isIP || isFIUR) && (
              <Row>
                <FieldBlock
                  label={
                    <>
                      Наименование проверяемого лица
                      <ErknmFieldMark fieldName={organizationNameFieldName} />
                    </>
                  }
                  color="black"
                >
                  <TextareaField
                    name={organizationNameFieldName}
                    resize="none"
                    inputClassName={styles.OrganizationNameInput}
                    clearable={isOrganizationNameFieldEnabled && organizationAutocomplete}
                    disabled={!isOrganizationNameFieldEnabled}
                    onClear={handleAutoCompleteClear}
                  />
                  {!smevRequestStatus &&
                    isOrganizationNameFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIUR && (
              <Row>
                <FieldBlock
                  label={
                    <>
                      Наименование проверяемого лица
                      <ErknmFieldMark fieldName={organizationNameFieldName} />
                    </>
                  }
                  color="black"
                >
                  <TextInputField name={organizationNameFieldName} disabled={watchOnly} />
                </FieldBlock>
              </Row>
            )}
            {(isUR || isFIUR) && (
              <Row>
                <FieldBlock label="Адрес места нахождения" color="black">
                  <TextInputField name={locationAddressFieldName} disabled={true} />
                  {!smevRequestStatus &&
                    isFnsAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIP && (
              <Row>
                <FieldBlock label="Место регистрации учредителя" color="black">
                  <TextInputField name={factualAddressFieldName} disabled={true} />
                  {!smevRequestStatus &&
                    isWorkAddressFnsError(checkSubjectResult) &&
                    renderFnsErvkEqualsError(
                      checkSubjectResult,
                      lookupData,
                      organizationAutocomplete,
                      styles.FnsMessage,
                    )}
                </FieldBlock>
              </Row>
            )}
            {isIFIZ && (
              <Row>
                <FieldBlock label="Сведения о документе, удостоверяющем личность" color="black" id={documentBlockId}>
                  <TextInputField name={documentFieldName} parse={parsePassport} disabled={watchOnly} />
                </FieldBlock>
              </Row>
            )}
            {(isIFIZ || isIUR) && (
              <Row>
                <FieldBlock
                  label={
                    <>
                      {isIFIZ
                        ? 'Гражданство (при наличии)'
                        : 'Государство, в котором указанное юридическое лицо зарегистрировано'}
                      <ErknmFieldMark fieldName={countryFieldName} />
                    </>
                  }
                  color="black"
                  id={countryBlockId}
                >
                  <SelectInputFieldAutoOptions
                    id={countryBlockId}
                    name={countryFieldName}
                    isClearable={false}
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.COUNTRIES}
                    transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                    disabled={watchOnly}
                  />
                </FieldBlock>
              </Row>
            )}
            {isUR && <KnmSubjectIsNko name={name} ogrn={ogrn} subjectType={subjectType} />}
            {!isIUR && (
              <Row margin="none">
                <KnmMspField
                  id={`${name}mspCode`}
                  name={`${name}mspCode`}
                  value={mspCode}
                  inn={inn}
                  mspRegistryDate={mspRegistryDate}
                  formRef={formRef}
                  isShowMessages={false}
                  ervkId={ervkId}
                />
              </Row>
            )}
            {(isUR || isIP || isFIUR) && (
              <Row>
                <KnmSonkoField
                  inn={inn}
                  ogrn={ogrn}
                  name={isSonkoName}
                  value={isSonko}
                  subjectType={subjectType}
                  id={`${name}isSonko`}
                  formRef={formRef}
                  isShowMessages={false}
                />
              </Row>
            )}
            {isUR && (
              <>
                <Row>
                  <Field name={itAccreditationResponseName}>{() => null}</Field>
                  <Field name={smevItAccreditationRequestStatusName}>{() => null}</Field>
                  <Field name={isPresentInItRegistryName}>{() => null}</Field>
                  <FieldBlock label="Наличие в реестре аккредитованных IT-организаций" placementLabel="left">
                    <TextInput className="shared-row-field" disabled value={isItText()} />
                  </FieldBlock>
                </Row>
                {isIt && itAccreditationResponse && (
                  <KnmSubjectItAccreditation itAccreditationResponse={itAccreditationResponse} />
                )}
              </>
            )}
          </>
        )}
      </>
    );
  },
);
